import { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/api';

type DebugStatus = 'ACTIVE' | 'WARNING' | 'CRITICAL' | 'EXPIRED';
type LastSource = 'mouse' | 'keyboard' | 'click' | 'scroll' | 'touch' | 'api-active' | 'unknown';

interface BackendSessionDebugPayload {
  timeoutMinutes: number;
  lastActivity: string;
  now: string;
  secondsInactive: number;
  secondsUntilExpiry: number;
  passiveRequestsCount: number;
  activeRequestsCount: number;
}

export interface SessionDebugState {
  timeoutMinutes: number;
  timeoutSeconds: number;
  secondsUntilExpiry: number;
  status: DebugStatus;
  backendLastActivity: string;
  frontendLastEvent: string;
  secondsSinceFrontendEvent: number;
  lastSource: LastSource;
  passiveRequestsCount: number;
  activeRequestsCount: number;
}

const DEFAULT_TIMEOUT_MINUTES = 15;
const DEFAULT_STATE: SessionDebugState = {
  timeoutMinutes: DEFAULT_TIMEOUT_MINUTES,
  timeoutSeconds: DEFAULT_TIMEOUT_MINUTES * 60,
  secondsUntilExpiry: DEFAULT_TIMEOUT_MINUTES * 60,
  status: 'ACTIVE',
  backendLastActivity: '-',
  frontendLastEvent: '-',
  secondsSinceFrontendEvent: 0,
  lastSource: 'unknown',
  passiveRequestsCount: 0,
  activeRequestsCount: 0,
};

function resolveStatus(secondsUntilExpiry: number): DebugStatus {
  if (secondsUntilExpiry <= 0) return 'EXPIRED';
  if (secondsUntilExpiry < 15) return 'CRITICAL';
  if (secondsUntilExpiry <= 60) return 'WARNING';
  return 'ACTIVE';
}

export function useSessionDebug(enabled: boolean) {
  const [state, setState] = useState<SessionDebugState>(DEFAULT_STATE);
  const [isOpen, setIsOpen] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const frontendLastEventMsRef = useRef<number>(Date.now());
  const lastSourceRef = useRef<LastSource>('unknown');
  const reqCountersRef = useRef<{ passive: number; active: number }>({ passive: 0, active: 0 });
  const backendErrorLoggedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const markFrontendEvent = (source: LastSource) => {
      frontendLastEventMsRef.current = Date.now();
      lastSourceRef.current = source;
      console.debug('[SessionDebug] frontend-event:', source, new Date(frontendLastEventMsRef.current).toISOString());
    };

    const onMouseMove = () => markFrontendEvent('mouse');
    const onKeyDown = () => markFrontendEvent('keyboard');
    const onClick = () => markFrontendEvent('click');
    const onScroll = () => markFrontendEvent('scroll');
    const onTouchStart = () => markFrontendEvent('touch');
    const onApiActivity = () => markFrontendEvent('api-active');
    const onApiRequest = (event: Event) => {
      const customEvent = event as CustomEvent<{ passive?: boolean; url?: string; method?: string }>;
      const isPassive = Boolean(customEvent.detail?.passive);
      if (isPassive) {
        reqCountersRef.current.passive += 1;
      } else {
        reqCountersRef.current.active += 1;
      }
      console.debug('[SessionDebug] api-request:', customEvent.detail);
    };
    
    // Escuchar cuando la sesión se refresca desde el modal
    const onSessionRefreshed = () => {
      console.debug('[SessionDebug] Session refreshed via modal');
      setSessionExpired(false);
      frontendLastEventMsRef.current = Date.now();
      const frontendIso = new Date(frontendLastEventMsRef.current).toISOString();
      setState((prev) => ({
        ...prev,
        frontendLastEvent: frontendIso,
        secondsSinceFrontendEvent: 0,
        status: 'ACTIVE',
      }));
    };

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('click', onClick, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('api:activity', onApiActivity as EventListener);
    window.addEventListener('api:request', onApiRequest as EventListener);
    window.addEventListener('session:refreshed', onSessionRefreshed as EventListener);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('click', onClick);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('api:activity', onApiActivity as EventListener);
      window.removeEventListener('api:request', onApiRequest as EventListener);
      window.removeEventListener('session:refreshed', onSessionRefreshed as EventListener);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || sessionExpired) return;

    const poll = async () => {
      const frontendLastEventMs = frontendLastEventMsRef.current;
      const frontendIso = new Date(frontendLastEventMs).toISOString();
      const secondsSinceFrontendEvent = Math.max(Math.floor((Date.now() - frontendLastEventMs) / 1000), 0);

      try {
        const response = await api.get<BackendSessionDebugPayload>('/debug/session', {
          headers: { 'X-Passive-Request': 'true' },
        });
        const payload = response.data;
        backendErrorLoggedRef.current = false;
        const status = resolveStatus(payload.secondsUntilExpiry);

        setState({
          timeoutMinutes: payload.timeoutMinutes,
          timeoutSeconds: payload.timeoutMinutes * 60,
          secondsUntilExpiry: payload.secondsUntilExpiry,
          status,
          backendLastActivity: payload.lastActivity,
          frontendLastEvent: frontendIso,
          secondsSinceFrontendEvent,
          lastSource: lastSourceRef.current,
          passiveRequestsCount: reqCountersRef.current.passive,
          activeRequestsCount: reqCountersRef.current.active,
        });
      } catch (error: any) {
        // Si es 401, la sesión expiró - detener polling permanentemente
        if (error?.response?.status === 401) {
          if (!sessionExpired) {
            console.debug('[SessionDebug] Session expired (401), stopping polling');
            setSessionExpired(true);
            setState((prev) => ({ ...prev, status: 'EXPIRED', secondsUntilExpiry: 0 }));
          }
          return;
        }

        if (!backendErrorLoggedRef.current) {
          backendErrorLoggedRef.current = true;
          console.debug('[SessionDebug] polling failed:', error);
        }

        setState((prev) => {
          const timeoutSeconds = prev.timeoutMinutes * 60;
          const localSecondsUntilExpiry = Math.max(timeoutSeconds - secondsSinceFrontendEvent, 0);
          return {
            ...prev,
            timeoutSeconds,
            secondsUntilExpiry: localSecondsUntilExpiry,
            status: resolveStatus(localSecondsUntilExpiry),
            frontendLastEvent: frontendIso,
            secondsSinceFrontendEvent,
            lastSource: lastSourceRef.current,
            passiveRequestsCount: reqCountersRef.current.passive,
            activeRequestsCount: reqCountersRef.current.active,
          };
        });
      }
    };

    poll();
    const intervalId = window.setInterval(poll, 5000);
    return () => window.clearInterval(intervalId);
  }, [enabled, sessionExpired]);

  const colors = useMemo(() => {
    if (state.status === 'EXPIRED') return 'text-red-700 border-red-300 bg-red-50';
    if (state.status === 'CRITICAL') return 'text-red-600 border-red-200 bg-red-50';
    if (state.status === 'WARNING') return 'text-amber-700 border-amber-300 bg-amber-50';
    return 'text-emerald-700 border-emerald-300 bg-emerald-50';
  }, [state.status]);

  return {
    state,
    isOpen,
    setIsOpen,
    colors,
    sessionExpired,
  };
}
